import { getSupabaseClient, toPublicUser } from "@/lib/supabase-direct";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const SESSION_STORAGE_KEY = "drabpe_supabase_session";

type SupabaseSession = {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_in?: number;
  expires_at?: number;
  user?: any;
};

type SupabaseAuthResponse = {
  user?: any;
  session?: SupabaseSession | null;
  access_token?: string;
  refresh_token?: string;
};

function getAuthBaseUrl() {
  const runtimeUrl = typeof window !== "undefined" ? window.DRABPE_CONFIG?.SUPABASE_URL : undefined;
  if (!SUPABASE_URL && !runtimeUrl) {
    throw new Error("VITE_SUPABASE_URL is missing.");
  }
  return (SUPABASE_URL || runtimeUrl)!.replace(/\/$/, "");
}

function getAnonKey() {
  const runtimeKey = typeof window !== "undefined" ? window.DRABPE_CONFIG?.SUPABASE_ANON_KEY : undefined;
  if (!SUPABASE_ANON_KEY && !runtimeKey) {
    throw new Error("VITE_SUPABASE_ANON_KEY is missing.");
  }
  return SUPABASE_ANON_KEY || runtimeKey!;
}

function storeSession(session: SupabaseSession | null) {
  if (typeof window === "undefined") return;
  if (!session) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

function readSession(): SupabaseSession | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SupabaseSession;
  } catch {
    return null;
  }
}

function normalizeSession(response: SupabaseAuthResponse): SupabaseSession | null {
  if (response.session) return response.session;
  if (response.access_token && response.refresh_token) {
    return {
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      token_type: "bearer",
      user: response.user,
    };
  }
  return null;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") || "";
  const bodyText = await response.text();
  if (!response.ok) {
    if (contentType.includes("application/json")) {
      try {
        const data = JSON.parse(bodyText);
        throw new Error(data?.msg || data?.message || data?.error_description || data?.error || "Request failed");
      } catch (parseError) {
        throw parseError instanceof Error ? parseError : new Error("Request failed");
      }
    }

    const snippet = bodyText.slice(0, 160).replace(/\s+/g, " ").trim();
    throw new Error(snippet || `Request failed with status ${response.status}`);
  }

  if (!bodyText) return {} as T;
  if (!contentType.includes("application/json")) {
    throw new Error(`Unexpected response from Supabase: ${bodyText.slice(0, 120)}`);
  }
  return JSON.parse(bodyText) as T;
}

async function syncLibrarySession(session: SupabaseSession | null) {
  const supabase = getSupabaseClient();
  if (!session?.access_token || !session?.refresh_token) return;
  try {
    await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
  } catch {
    // Ignore sync failures; fetch-based auth still works.
  }
}

async function authRequest<T>(
  path: string,
  options: RequestInit = {},
  includeApiKey = true,
): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");
  if (includeApiKey) {
    headers.set("apikey", getAnonKey());
  }

  const session = readSession();
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }

  const response = await fetch(`${getAuthBaseUrl()}${path}`, {
    ...options,
    headers,
    credentials: "omit",
  });

  return parseResponse<T>(response);
}

export async function restoreSupabaseSession() {
  const { data } = await getSupabaseClient().auth.getSession();
  return data.session;
}

export async function getCurrentAuthUser() {
  const supabase = getSupabaseClient();
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) return null;
  const user = authData.user;
  const { data: profileRow } = await supabase
    .from("profiles")
    .select("user_id, username, role, avatar_url")
    .eq("user_id", user.id)
    .maybeSingle();

  return toPublicUser(user, profileRow);
}

export async function signInWithPassword(email: string, password: string, captchaToken: string) {
  const { data, error } = await getSupabaseClient().auth.signInWithPassword({
    email,
    password,
    options: { captchaToken },
  });
  if (error) throw error;
  return data;
}

export async function signUpWithPassword(email: string, password: string, username: string, captchaToken: string) {
  const { data, error } = await getSupabaseClient().auth.signUp({
    email,
    password,
    options: { data: { username }, captchaToken, emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
  return data;
}

export async function signInWithGoogle(captchaToken: string) {
  const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
    provider: "google",
    options: { captchaToken, redirectTo: `${window.location.origin}/directory` },
  });
  if (error) throw error;
  if (!data.url) throw new Error("Google sign-in is not configured.");
  window.location.assign(data.url);
}

export async function signOut() {
  storeSession(null);
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export function getStoredAuthSession() {
  return readSession();
}
